import { useEffect, useState } from "react";
import {
  Button,
  Form,
  Grid,
  Input,
  message,
  Modal,
  Select,
  Typography,
} from "antd";
import { X } from "lucide-react";
import {
  CATEGORY_OPTIONS,
  normalizeResidencyStatusToCategoryKey,
} from "@/constants/category";
import type { ApplicationItem } from "@/types/Application/application";
import type { Village } from "@/types/MassterData/village";
import { UpdateRegistrationDetail } from "@/services/application.service";
import {
  applicationSchema,
  type EditApplicationFormValues,
} from "./schema/application.schema";
interface EditApplicationModalProps {
  open: boolean;
  application: ApplicationItem | null;
  villageList: Village[];
  onClose: () => void;
  onBack: () => void;
  onDelete?: () => void;
  onSuccess?: () => void;
}

const { Title } = Typography;
const { useBreakpoint } = Grid;

const modalStyles = {
  container: { padding: 0 },
  header: { padding: 0, marginBottom: 0 },
  body: { padding: 24 },
  footer: { padding: "0 16px 16px" },
} as const;

export function EditApplicationModal({
  open,
  application,
  villageList,
  onClose,
  onBack,
  onDelete,
  onSuccess,
}: EditApplicationModalProps) {
  const [form] = Form.useForm();
  const screens = useBreakpoint();
  const [saving, setSaving] = useState(false);
  const canDeleteApplication = application?.registrationStatus !== "pending";

  const KAMPUNG_OPTIONS = villageList.map((village) => ({
    label: village.name,
    value: village.mdVillageId,
  }));

  useEffect(() => {
    if (!open || !application) {
      return;
    }

    const [firstName = "", ...lastNameParts] = (application.name ?? "").split(
      /\s+/,
    );

    const normalizeVillageName = (name?: string) => {
      return name
        ?.trim()
        .toLowerCase()
        .replace(/^kg\.?\s+/i, "");
    };

    const matchedVillage = villageList.find(
      (village) =>
        normalizeVillageName(village.name) ===
        normalizeVillageName(application.villageName),
    );

    form.setFieldsValue({
      firstName,
      lastName: lastNameParts.join(" "),
      myKadPassport: application.identityNo ?? "",
      phoneNumber: application.phoneNumber ?? "",
      categoryKey: normalizeResidencyStatusToCategoryKey(
        application.residencyStatus,
      ),
      kampung: matchedVillage?.mdVillageId ?? undefined,
      address1: application.address1 ?? "",
      address2: application.address2 ?? null,
    });
  }, [application, form, open, villageList]);

  const handleSubmit = async (values: EditApplicationFormValues) => {
    if (!application) return;

    // Validate using Zod
    const result = applicationSchema.safeParse(values);

    if (!result.success) {
      form.setFields(
        result.error.issues.map((issue) => ({
          name: issue.path,
          errors: [issue.message],
        })),
      );

      return;
    }

    // Use validated values from Zod
    const validatedValues = result.data;

    try {
      setSaving(true);

      const payload = {
        registrationId: application.registrationId,
        firstName: validatedValues.firstName,
        lastName: validatedValues.lastName,
        identityNo: validatedValues.myKadPassport,
        phoneNumber: validatedValues.phoneNumber,
        categoryKey: validatedValues.categoryKey,
        mdVillageId: validatedValues.kampung,
        address1: validatedValues.address1,
        address2: validatedValues.address2 ?? null,
      };

      await UpdateRegistrationDetail(payload);

      message.success("Maklumat berjaya dikemaskini.");

      onSuccess?.();

      form.resetFields();
      onClose();
    } catch (error) {
      console.error("Update registration detail error:", error);

      message.error(
        error instanceof Error ? error.message : "Gagal mengemaskini maklumat.",
      );
    } finally {
      setSaving(false);
    }
  };

  const modalEditTitle = (
    <div className="flex items-center justify-between p-4 sm:p-6 bg-[#E5F2FF] rounded-tl-2xl rounded-tr-2xl">
      <Title
        level={2}
        style={{ margin: 0, fontSize: screens.xs ? "18px" : "22px" }}
      >
        Kemaskini
      </Title>
      <button type="button" className="cursor-pointer" onClick={onClose}>
        <X size={28} aria-hidden="true" />
      </button>
    </div>
  );

  return (
    <Modal
      title={modalEditTitle}
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      styles={modalStyles}
      width={1200}
      centered
    >
      {/* <div className="flex justify-end mb-4">
    
      </div> */}

      {application && (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
        >
          <div className="grid md:grid-cols-2 gap-x-6 md:gap-x-10">
            <Form.Item
              label={renderLabel("Kategori", true)}
              name="categoryKey"
              rules={requiredRule("Sila pilih kategori")}
            >
              <Select
                size="large"
                options={CATEGORY_OPTIONS}
                showSearch
                placeholder="Pilih kategori"
                optionFilterProp="label"
              />
            </Form.Item>

            {canDeleteApplication ? (
              <div className="flex justify-end items-center mb-4">
                <Button
                  htmlType="button"
                  type="primary"
                  style={{
                    height: 48,
                    borderRadius: 8,
                    minWidth: 16,
                    backgroundColor: "rgb(204, 0, 0)",
                    color: "#fff",
                  }}
                  onClick={() => {
                    if (application?.registrationStatus === "pending") {
                      return;
                    }

                    onDelete?.();
                  }}
                >
                  Padam Data
                </Button>
              </div>
            ) : (
              <div className="mb-4" aria-hidden="true" />
            )}

            <Form.Item
              label={renderLabel("Nama Pertama", true)}
              name="firstName"
              rules={requiredRule("Sila masukkan nama pertama")}
            >
              <Input
                size="large"
                maxLength={50}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^A-Za-z ]/g, "")
                    .replace(/\s+/g, " ")
                    .toUpperCase()
                    .slice(0, 50);

                  form.setFieldValue("firstName", value);
                }}
              />
            </Form.Item>

            <Form.Item
              label={renderLabel("Nama Akhir", true)}
              name="lastName"
              rules={requiredRule("Sila masukkan nama akhir")}
            >
              <Input
                size="large"
                maxLength={50}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^A-Za-z ]/g, "")
                    .replace(/\s+/g, " ")
                    .toUpperCase()
                    .slice(0, 50);

                  form.setFieldValue("lastName", value);
                }}
              />
            </Form.Item>

            <Form.Item
              label={renderLabel("Nombor MyKad atau pasport", false)}
              name="myKadPassport"
            >
              <Input
                size="large"
                maxLength={15}
                onChange={(e) => {
                  const value = e.target.value
                    .replace(/[^a-zA-Z0-9]/g, "")
                    .toUpperCase()
                    .slice(0, 12);

                  form.setFieldValue("myKadPassport", value);
                }}
              />
            </Form.Item>

            <Form.Item
              label={renderLabel("Nombor Telefon", false)}
              name="phoneNumber"
            >
              <Input
                size="large"
                maxLength={15}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 11);

                  form.setFieldValue("phoneNumber", value);
                }}
              />
            </Form.Item>

            <div className="md:col-span-2 grid md:grid-cols-3 gap-x-6 md:gap-x-10">
              <Form.Item
                className="md:col-span-1"
                label={renderLabel("Kampung", true)}
                name="kampung"
                rules={requiredRule("Sila pilih kampung")}
              >
                <Select
                  size="large"
                  options={KAMPUNG_OPTIONS}
                  showSearch
                  placeholder="Pilih kampung"
                />
              </Form.Item>
              <Form.Item
                className="md:col-span-2"
                label={renderLabel("Alamat", true)}
                rules={requiredRule("Sila masukkan alamat")}
              >
                <div className="flex flex-col gap-3">
                  <Form.Item
                    name="address1"
                    noStyle
                    rules={requiredRule("Sila masukkan alamat")}
                  >
                    <Input size="large" maxLength={85} />
                  </Form.Item>

                  <Form.Item name="address2" noStyle>
                    <Input size="large" maxLength={170} />
                  </Form.Item>
                </div>
              </Form.Item>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 md:gap-8 mt-10">
            <Button
              color="default"
              variant="solid"
              size="large"
              onClick={() => {
                form.resetFields();
                onBack();
              }}
            >
              Batal
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              loading={saving}
              disabled={saving}
            >
              Simpan
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
}

function renderLabel(text: string, required = false) {
  return (
    <span className="text-sm text-[#666] leading-none font-semibold">
      {text}
      {required && <span className="text-red-500">*</span>}
    </span>
  );
}

function requiredRule(message: string) {
  return [{ required: true, message }];
}
